import React, { useState } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Snackbar,
  useTheme,
  Alert,
} from '@mui/material';

const API_URL = process.env.REACT_APP_API_URL;

function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState('');
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const theme = useTheme();

  const handleSubmit = async (event) => {
    event.preventDefault();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address.');
      setOpenSnackbar(true);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/newsletter_subscribe/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      const data = await response.json();

      if (response.ok) {
        setIsSubscribed(true);
        setError('');
      } else {
        if (data.detail === 'This email is already subscribed.') {
          setError(data.detail);
        } else {
          setError(data.detail || 'Something went wrong. Please try again.');
        }
        setIsSubscribed(false);
      }

      setOpenSnackbar(true);
      setEmail('');
    } catch {
      setIsSubscribed(false);
      setError('There was an error processing your request. Please try again later.');
      setOpenSnackbar(true);
    }
  };

  return (
    <Container
      maxWidth="md"
      sx={{
        textAlign: 'center',
        mt: 15,
        mb: 15,
        p: { xs: 3, md: 6 },
        borderRadius: 4,
        backdropFilter: 'blur(15px)',
        // background: 'rgba(65, 62, 62, 0.05)',
        background: "linear-gradient(135deg, #000000, #0f172a)",
        boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
      }}
    >
      <Typography
        variant="h3"
        sx={{
          fontWeight: 700,
          background: 'linear-gradient(90deg, #00DBDE, #FC00FF)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent',
          mb: 2,
          letterSpacing: 2,
        }}
      >
        Stay Updated with Us!
      </Typography>

      <Typography
        variant="body1"
        sx={{
          color: '#ccc',
          fontSize: '1.1rem',
          mb: 4,
        }}
      >
        Subscribe to our newsletter for the latest updates, tips, and insights delivered right to your inbox.
      </Typography>

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: 2,
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <TextField
          label="Enter your email"
          variant="outlined"
          fullWidth
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={Boolean(error)}
          helperText={error}
          required
          sx={{
            input: { color: '#fff' },
            label: { color: '#bbb' },
            backgroundColor: 'rgba(255,255,255,0.05)',
            borderRadius: 2,
            '& .MuiOutlinedInput-root': {
              '&:hover fieldset': {
                borderColor: '#00DBDE',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#FC00FF',
              },
            },
            '& .MuiFormHelperText-root': {
              color: error === 'This email is already subscribed.' ? '#1976d2' : '#f44336',
            },
          }}
        />

        <Button
          type="submit"
          variant="contained"
          size="large"
          sx={{
            mt: { xs: 2, sm: 0 },
            px: 5,
            py: 1.5,
            fontWeight: 'bold',
            fontSize: '1rem',
            color: '#fff',
            background: 'linear-gradient(to right, #00dbde, #fc00ff)',
            borderRadius: 8,
            transition: '0.3s',
            '&:hover': {
              background: 'linear-gradient(to left, #00dbde, #fc00ff)',
              boxShadow: '0px 0px 15px 3px rgba(255,255,255,0.2)',
            },
          }}
        >
          Subscribe Now
        </Button>
      </Box>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setOpenSnackbar(false)}
          severity={
            isSubscribed
              ? 'success'
              : error === 'This email is already subscribed.'
              ? 'info'
              : 'error'
          }
          sx={{
            backgroundColor: isSubscribed
              ? '#4caf50'
              : error === 'This email is already subscribed.'
              ? '#1976d2'
              : '#f44336',
            color: '#fff',
            borderRadius: 2,
            fontWeight: 500,
          }}
        >
          {isSubscribed
            ? 'Thank you for subscribing! You’ll hear from us soon.'
            : error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default NewsletterSection;
